import { Box, Typography } from "@mui/material";
import { useDealContext } from "../../deal/context";
import { CompanyTitle } from "../components/CompanyTitle";
import { NurtureList } from "../components/nurture/NurtureList";
import { NurtureGraph } from "../components/nurture/NurtureGraph";
import ProjectHttpClient from "../../../core/infrastructure/clients/v1/ProjectHttpClient";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { ITouchpoint, TouchpointStatus, TouchpointType } from "../../../types";
import { InsightsList } from "../components/insights/InsightsList";
import { CompanyTouchpoints } from "./CompanyTouchpoints";

export const CompanyInsights = () => {

    const { dealContext } = useDealContext();
    const project = dealContext.project;
    const viewerRole = dealContext.role;

    const client = new ProjectHttpClient();
    const { getAccessTokenSilently } = useAuth0();
    const [touchpoints, setTouchpoints] = useState([]);

    useEffect(() => {
        const fetchTouchpoints = async () => {
            const accessToken = await getAccessTokenSilently();
            const { data, error } = await client.getProjectTouchpoints(project.id, accessToken);
            setTouchpoints(data.touchpoints);
        }

        if (project) {
            fetchTouchpoints();
        }
    }, [project]);

    if (!project.insights_enabled) {
        return <CompanyTouchpoints />
    }

    // Calculate the first day of the next 3 months
    const getFirstDayOfNextMonth = (monthsAhead: number) => {
        const date = new Date();
        date.setMonth(date.getMonth() + monthsAhead);
        date.setDate(1);
        date.setHours(0, 0, 0, 0);
        return date.toISOString();
    };

    const scheduledTouchpoints = [
        {
            id: -1, // Temporary negative ID for scheduled touchpoints
            uuid: `scheduled-${Date.now()}-1`,
            name: `Monthly Pulse Check - ${new Date(getFirstDayOfNextMonth(1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
            project: project,
            type: TouchpointType.AUTO,
            status: TouchpointStatus.SCHEDULED,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            scheduled_at: getFirstDayOfNextMonth(1),
            started_at: getFirstDayOfNextMonth(1),
            completed_at: null,
            viewpoints: [],
            created_by: null,
            analysis_url: null,
            advisor_analysis_url: null,
            product_description: null,
            suppressed_questions: null,
            suppressed_sections: null,
            product_description_section: null,
            survey_links: null,
            default_survey_url: null,
            alchemer_survey_id: null,
            survey_questions_json: null,
            survey_layout_json: null,
            advisor_activity: null,
        },
        {
            id: -2,
            uuid: `scheduled-${Date.now()}-2`,
            name: `Monthly Pulse Check - ${new Date(getFirstDayOfNextMonth(2)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
            project: project,
            type: TouchpointType.AUTO,
            status: TouchpointStatus.SCHEDULED,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            scheduled_at: getFirstDayOfNextMonth(2),
            started_at: getFirstDayOfNextMonth(2),
            completed_at: null,
            viewpoints: [],
            created_by: null,
            analysis_url: null,
            advisor_analysis_url: null,
            product_description: null,
            suppressed_questions: null,
            suppressed_sections: null,
            product_description_section: null,
            survey_links: null,
            default_survey_url: null,
            alchemer_survey_id: null,
            survey_questions_json: null,
            survey_layout_json: null,
            advisor_activity: null,
        },
        {
            id: -3,
            uuid: `scheduled-${Date.now()}-3`,
            name: `Monthly Pulse Check - ${new Date(getFirstDayOfNextMonth(3)).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`,
            project: project,
            type: TouchpointType.AUTO,
            status: TouchpointStatus.SCHEDULED,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            scheduled_at: getFirstDayOfNextMonth(3),
            started_at: getFirstDayOfNextMonth(3),
            completed_at: null,
            viewpoints: [],
            created_by: null,
            analysis_url: null,
            advisor_analysis_url: null,
            product_description: null,
            suppressed_questions: null,
            suppressed_sections: null,
            product_description_section: null,
            survey_links: null,
            default_survey_url: null,
            alchemer_survey_id: null,
            survey_questions_json: null,
            survey_layout_json: null,
            advisor_activity: null,
        }
    ] as ITouchpoint[]

    return (
        <Box>
            <CompanyTitle viewerRole={viewerRole} project={project} section="Insights" subheading="Monthly feedback loops gather insights on the changing needs and priorities of advisors. You can also add your own feedback loop to poll advisors about new products, programs, etc. Each feedback loop prompts advisors to calendar intros and demos." />
            <NurtureGraph touchpoints={[...touchpoints, ...scheduledTouchpoints]} />
            <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "1.1rem", margin: "1rem 0" }}>Currently Active</Typography>
            <InsightsList section="Active" touchpoints={[
                ...touchpoints?.filter((touchpoint: any) => touchpoint.status === "active"),
            ]} />
            <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "1.1rem", margin: "1rem 0" }}>Previously Completed</Typography>
            <InsightsList section="Completed" touchpoints={[
                ...touchpoints?.filter((touchpoint: any) => touchpoint.status === "completed"),
            ]} />
            <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "1.1rem", margin: "1rem 0" }}>Scheduled</Typography>
            <InsightsList section="Scheduled" touchpoints={[
                ...touchpoints?.filter((touchpoint: any) => touchpoint.status === "scheduled"),
                ...scheduledTouchpoints,
                ...touchpoints?.filter((touchpoint: any) => touchpoint.status === "draft"),
            ]} />

        </Box>
    )
}